import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Loading, fmtTime } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Flag, Clock, X, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { MixedSheet } from "@/components/MixedSheet";

export default function ExamPlayer() {
  const { hwId } = useParams();
  const navigate = useNavigate();
  const [hw, setHw] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState([]);
  const [current, setCurrent] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [hideTimer, setHideTimer] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const startedRef = useRef(Date.now());

  useEffect(() => {
    (async () => {
      try {
        const { data: h } = await api.get(`/homework/${hwId}`);
        if (h.questions?.[0]?.type === "writing") { navigate(`/student/writing/${hwId}`); return; }
        const { data: att } = await api.post("/attempts/start", { homework_id: hwId });
        if (att.status === "submitted") { navigate(`/student/result/${att.id}`); return; }
        setHw(h);
        setAttemptId(att.id);
        if (h.mode === "timed") setRemaining(h.time_limit * 60);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        navigate("/student");
      }
    })();
  }, [hwId, navigate]);

  const doSubmit = useCallback(async () => {
    if (submitting || !attemptId) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/attempts/${attemptId}/submit`, { answers, flagged, time_taken_seconds: seconds });
      toast.success("Homework submitted");
      navigate(`/student/result/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  }, [submitting, attemptId, answers, flagged, seconds, navigate]);

  // elapsed + countdown
  useEffect(() => {
    const t = setInterval(() => {
      setSeconds((s) => s + 1);
      setRemaining((r) => (r == null ? r : Math.max(0, r - 1)));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { if (remaining === 0) { toast.warning("Time's up — submitting"); doSubmit(); } }, [remaining, doSubmit]);

  // periodic autosave
  useEffect(() => {
    if (!attemptId) return;
    const t = setInterval(() => { api.put(`/attempts/${attemptId}`, { answers, flagged, time_taken_seconds: seconds }).catch(() => {}); }, 15000);
    return () => clearInterval(t);
  }, [attemptId, answers, flagged, seconds]);

  if (!hw) return <Loading />;

  const questions = hw.questions;
  const q = questions[current];
  const answeredCount = questions.filter((x) => answers[x.id]).length;
  const setAnswer = (key) => setAnswers({ ...answers, [q.id]: key });
  const toggleFlag = () => setFlagged((f) => (f.includes(q.id) ? f.filter((x) => x !== q.id) : [...f, q.id]));

  const timeTone = remaining == null ? "" : remaining <= 60 ? "timer-critical text-white" : remaining <= 300 ? "bg-[#FFFBEB] text-[#B45309]" : "bg-[#F5F3FF] text-[#5B21B6]";
  const showTime = !hideTimer || (remaining != null && remaining <= 60);
  const TimerEl = remaining != null && (
    <div className="flex items-center gap-1">
      {showTime && <div className={`flex items-center gap-1.5 rounded-xl px-3 py-2 font-mono-t text-lg font-bold ${timeTone}`} data-testid="exam-timer"><Clock className="h-4 w-4" /> {fmtTime(remaining)}</div>}
      <button type="button" onClick={() => setHideTimer((h) => !h)} data-testid="toggle-timer"
        className="flex items-center gap-1 rounded-xl px-2.5 py-2 text-xs font-bold text-[#4B2BB8] hover:bg-[#F3F0FF]">
        {hideTimer ? <><Eye className="h-4 w-4" /> Show time</> : <><EyeOff className="h-4 w-4" /> Hide</>}
      </button>
    </div>
  );
  const optCount = hw.option_count || 5;
  const optLetters = Array.from({ length: optCount }, (_, i) => String.fromCharCode(65 + i));
  const fileUrl = hw.file_url ? `${BACKEND_URL}${hw.file_url}` : null;
  const isPdf = hw.format === "pdf";
  const isSaq = hw.format === "saq";
  const isMixed = hw.format === "mixed";

  const TopBar = (
    <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#E9E5F5] bg-white/95 px-4 py-3 backdrop-blur-md sm:px-6">
      <div className="flex items-center gap-3">
        <Logo withText={false} />
        <div className="hidden sm:block">
          <div className="text-sm font-bold text-[#1E1B2E]">{hw.title}</div>
          <div className="text-xs text-[#948EAB]">{hw.section_name} · {hw.mode === "timed" ? "Timed" : "Practice"}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {TimerEl}
        <Button onClick={() => setConfirm(true)} data-testid="submit-homework-btn" className="h-10 rounded-xl ember-gradient font-bold shadow-md shadow-violet-500/25">Submit</Button>
      </div>
    </header>
  );

  const SubmitDialog = (
    <AlertDialog open={confirm} onOpenChange={setConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Submit exam?</AlertDialogTitle>
          <AlertDialogDescription>
            You have answered <b className="text-[#1E1B2E]">{answeredCount}/{questions.length}</b> questions.
            {answeredCount < questions.length && <span className="mt-1 block text-[#D97706]">{questions.length - answeredCount} question(s) are unanswered.</span>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="continue-working">Continue working</AlertDialogCancel>
          <AlertDialogAction onClick={doSubmit} data-testid="confirm-submit" className="ember-gradient"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Submit</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isPdf || isSaq || isMixed) {
    return (
      <div className="min-h-screen bg-[#F8F7FC]">
        {TopBar}
        <div className="mx-auto grid max-w-[1400px] gap-4 px-3 py-4 sm:px-4 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="ember-card overflow-hidden p-0">
              {fileUrl ? (
                <iframe title="exam" src={`${fileUrl}#toolbar=1&view=FitH`} data-testid="exam-pdf" className="h-[78vh] w-full rounded-2xl" />
              ) : (
                <div className="grid h-[70vh] place-items-center text-sm text-[#948EAB]">The exam document could not be loaded.</div>
              )}
            </div>
            <p className="mt-2 px-1 text-xs text-[#948EAB]">Read the exam in the document above and record your answers in the sheet. Your answers are never marked until you submit.</p>
          </div>
          <aside className="lg:col-span-4">
            <div className="ember-card p-5 lg:sticky lg:top-24">
              {isMixed ? <MixedSheet hw={hw} answers={answers} setAnswers={setAnswers} onFinish={() => setConfirm(true)} /> : <>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-heading font-bold text-[#1E1B2E]">Answer sheet</h3>
                <span className="text-xs font-semibold text-[#948EAB]">{answeredCount}/{questions.length}</span>
              </div>
              <div className="max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                {isSaq ? questions.map((x) => (
                  <div key={x.id} data-testid={`saq-row-${x.number}`} className="rounded-xl border border-[#EEEAF8] p-2.5">
                    <div className="mb-1 text-sm font-bold text-[#1E1B2E]">{x.label || `Response ${x.number}`}</div>
                    <textarea data-testid={`saq-input-${x.number}`} value={answers[x.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [x.id]: e.target.value })}
                      placeholder="Type your answer…" className="min-h-[90px] w-full rounded-lg border border-[#E9E5F5] p-2 text-sm outline-none focus:border-[#C4B5FD]" />
                  </div>
                )) : questions.map((x) => (
                  <div key={x.id} data-testid={`answer-row-${x.number}`} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 ${answers[x.id] ? "border-[#C4B5FD] bg-[#FAF9FE]" : "border-[#EEEAF8]"}`}>
                    <span className="w-8 shrink-0 text-sm font-bold text-[#1E1B2E]">{x.number}</span>
                    <div className="flex flex-wrap gap-1">
                      {(x.options?.length ? x.options.map((o) => o.key) : optLetters).map((l) => {
                        const sel = answers[x.id] === l;
                        return (
                          <button key={l} data-testid={`sheet-${x.number}-${l}`} onClick={() => setAnswers({ ...answers, [x.id]: sel ? undefined : l })}
                            className={`grid h-8 w-8 place-items-center rounded-lg border-2 text-xs font-bold transition-all ${sel ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E9E5F5] text-[#7C3AED] hover:border-[#C4B5FD]"}`}>{l}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={() => setConfirm(true)} className="mt-4 h-11 w-full rounded-xl ember-gradient font-bold">Submit exam</Button>
              </>}
            </div>
          </aside>
        </div>
        {SubmitDialog}
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#F8F7FC]">
      {/* top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#E9E5F5] bg-white/95 px-4 py-3 backdrop-blur-md sm:px-6">
        <div className="flex items-center gap-3">
          <Logo withText={false} />
          <div className="hidden sm:block">
            <div className="text-sm font-bold text-[#1E1B2E]">{hw.title}</div>
            <div className="text-xs text-[#948EAB]">{hw.section_name} · {hw.mode === "timed" ? "Timed" : "Practice"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {TimerEl}
          <Button onClick={() => setConfirm(true)} data-testid="submit-homework-btn" className="h-10 rounded-xl ember-gradient font-bold shadow-md shadow-violet-500/25">Submit</Button>
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-12">
        {/* question */}
        <div className="lg:col-span-8">
          <div className="ember-card p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-[#7C3AED]">Question {current + 1} of {questions.length}</span>
              <button onClick={toggleFlag} data-testid="flag-btn"
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${flagged.includes(q.id) ? "bg-[#FFFBEB] text-[#D97706]" : "text-[#948EAB] hover:bg-[#F5F3FF]"}`}>
                <Flag className="h-4 w-4" fill={flagged.includes(q.id) ? "#D97706" : "none"} /> {flagged.includes(q.id) ? "Flagged" : "Flag"}
              </button>
            </div>

            <p className="mt-4 whitespace-pre-wrap font-heading text-xl font-bold leading-relaxed text-[#1E1B2E]" data-testid="question-text">{q.text}</p>
            {q.image_url && <img src={q.image_url} alt="diagram" className="mt-4 max-h-80 rounded-xl border border-[#E9E5F5]" />}
            {q.has_diagram && !q.image_url && q.diagram_description && (
              <div className="mt-3 rounded-xl bg-[#F5F3FF] px-4 py-3 text-sm text-[#5B21B6]">Diagram: {q.diagram_description}</div>
            )}

            <div className="mt-6 space-y-3">
              {q.options.map((o) => {
                const selected = answers[q.id] === o.key;
                return (
                  <button key={o.key} onClick={() => setAnswer(o.key)} data-testid={`option-${o.key}`}
                    className={`flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${selected ? "border-[#7C3AED] bg-[#F5F3FF] shadow-sm" : "border-[#E9E5F5] hover:border-[#C4B5FD] hover:bg-[#FAF9FE]"}`}>
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl border-2 text-sm font-bold ${selected ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#DDD6FE] text-[#7C3AED]"}`}>{o.key}</span>
                    <span className="font-medium text-[#1E1B2E]">{o.text}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <Button variant="outline" disabled={current === 0} onClick={() => setCurrent((c) => c - 1)} data-testid="prev-btn" className="h-11 rounded-xl"><ChevronLeft className="mr-1 h-4 w-4" /> Previous</Button>
            {current < questions.length - 1
              ? <Button onClick={() => setCurrent((c) => c + 1)} data-testid="next-btn" className="h-11 rounded-xl ember-gradient font-bold">Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
              : <Button onClick={() => setConfirm(true)} className="h-11 rounded-xl ember-gradient font-bold">Review &amp; Submit</Button>}
          </div>
        </div>

        {/* navigator */}
        <aside className="lg:col-span-4">
          <div className="ember-card p-5 lg:sticky lg:top-24">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#1E1B2E]">Questions</h3>
              <span className="text-xs font-semibold text-[#948EAB]">{answeredCount}/{questions.length}</span>
            </div>
            <div className="grid grid-cols-6 gap-2 lg:grid-cols-5">
              {questions.map((x, i) => {
                const isCur = i === current;
                const ans = !!answers[x.id];
                const fl = flagged.includes(x.id);
                let cls = "border-[#E9E5F5] bg-white text-[#948EAB]";
                if (ans) cls = "border-transparent bg-[#059669] text-white";
                if (fl) cls = "border-[#D97706] bg-[#FFFBEB] text-[#B45309]";
                if (isCur) cls += " ring-2 ring-[#7C3AED] ring-offset-1";
                return (
                  <button key={x.id} onClick={() => setCurrent(i)} data-testid={`nav-q-${i}`}
                    className={`relative grid h-10 place-items-center rounded-xl border-2 text-sm font-bold transition-all ${cls}`}>
                    {i + 1}
                    {fl && <Flag className="absolute -right-1 -top-1 h-3 w-3 text-[#D97706]" fill="#D97706" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 space-y-1.5 text-xs text-[#524B6B]">
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[#059669]" /> Answered</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded border-2 border-[#E9E5F5] bg-white" /> Unanswered</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded border-2 border-[#D97706] bg-[#FFFBEB]" /> Flagged</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded ring-2 ring-[#7C3AED]" /> Current</div>
            </div>
          </div>
        </aside>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit homework?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered <b className="text-[#1E1B2E]">{answeredCount}/{questions.length}</b> questions.
              {answeredCount < questions.length && <span className="mt-1 block text-[#D97706]">{questions.length - answeredCount} question(s) are unanswered.</span>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="continue-working">Continue working</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} data-testid="confirm-submit" className="ember-gradient"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
